import cx from "classnames";
import { colors } from "constants/colors";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useId } from "react";

type GaugeProps = {
  value: number;
  label?: string;
  className?: string;
};

// Semicircle geometry: radius 86 centred at (100, 102).
const ARC_PATH = "M14,102 A86,86 0 0 1 186,102";
const ARC_LENGTH = 100;
const ARC_RADIUS = 86;
const ARC_CENTER_X = 100;
const ARC_CENTER_Y = 102;
const ARC_STROKE_WIDTH = 15;

const DEFAULT_LABEL = "Complete";

// Brand gauge gradient — exact reproduction of the Figma asset's
// `linear-gradient(51deg, #FF2727 -12.16%, #EA638C 53.16%, #5721FF 81.73%,
// #67AD67 171.54%, #1DF9D0 234.13%)` applied to the circle and rotated -90°.
// On screen the axis runs bottom-right → top-left; the vector + offsets below
// are that gradient line normalised into userSpace coords (see GRADIENT_VECTOR).
// Green/teal sit past the arc's sweep and never render — only red → pink →
// purple show — but the full stops are kept so colour varies along the arc
// (a short low-% segment still shows the purple↔pink transition).
const GRADIENT_STOPS = [
  { offset: "0", color: "#FF2727" },
  { offset: "0.2652", color: "#EA638C" },
  { offset: "0.3812", color: "#5721FF" },
  { offset: "0.7459", color: "#67AD67" },
  { offset: "1", color: "#1DF9D0" },
] as const;

const GRADIENT_VECTOR = { x1: 194.6, y1: 218.9, x2: -180.2, y2: -244.1 };

// Knob sized so its outer (white) edge sits flush inside the track width:
// KNOB_RADIUS + KNOB_STROKE_WIDTH / 2 ≈ ARC_STROKE_WIDTH / 2 (7.5).
const KNOB_RADIUS = 8;
const KNOB_STROKE_WIDTH = 1.6;

// Track inset shadow (Figma): 0 1.158px 2.317px rgba(0,0,0,0.10) inset,
// scaled from the 139px design to the 200-wide viewBox (×200/139).
const SHADOW_OFFSET_Y = 1.67;
const SHADOW_BLUR = 1.67;
const SHADOW_OPACITY = 0.1;

// Knob drop shadow — soft, near-even halo so the white ring separates from the
// light track on every side (a down-right-only offset left the top edge flat).
const KNOB_SHADOW_OFFSET_X = 0;
const KNOB_SHADOW_OFFSET_Y = 0.8;
const KNOB_SHADOW_BLUR = 2.2;
const KNOB_SHADOW_OPACITY = 0.28;

const clamp = (value: number): number => Math.max(0, Math.min(100, value));

const VALUE_ANIMATION = { duration: 0.6, ease: "easeOut" } as const;

export const Gauge = ({
  value,
  label = DEFAULT_LABEL,
  className,
}: GaugeProps) => {
  const gradientId = useId();
  const shadowId = useId();
  const knobShadowId = useId();
  const safeValue = clamp(value);

  // Point on the arc at the current value (180° at 0% → 0° at 100%).
  const knobAngle = Math.PI - (safeValue / 100) * Math.PI;
  const knobX = ARC_CENTER_X + ARC_RADIUS * Math.cos(knobAngle);
  const knobY = ARC_CENTER_Y - ARC_RADIUS * Math.sin(knobAngle);

  // Ease the displayed number up/down instead of snapping to each new value.
  const animatedValue = useMotionValue(safeValue);
  const displayText = useTransform(animatedValue, (v) => `${Math.round(v)}%`);
  const dashArray = useTransform(animatedValue, (v) => `${v} ${ARC_LENGTH}`);

  useEffect(() => {
    const controls = animate(animatedValue, safeValue, VALUE_ANIMATION);
    return () => controls.stop();
  }, [safeValue, animatedValue]);

  return (
    <div className={cx("t-max-w-full", className)}>
      <svg
        viewBox="0 0 200 110"
        className="t-block t-h-auto t-w-full t-overflow-visible t-font-sans"
        role="img"
        aria-label={`${safeValue}% ${label}`}
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1={GRADIENT_VECTOR.x1}
            y1={GRADIENT_VECTOR.y1}
            x2={GRADIENT_VECTOR.x2}
            y2={GRADIENT_VECTOR.y2}
            gradientUnits="userSpaceOnUse"
          >
            {GRADIENT_STOPS.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.color}
              />
            ))}
          </linearGradient>

          <filter id={shadowId} x="-50%" y="-50%" width="200%" height="200%">
            <feComponentTransfer in="SourceAlpha" result="inverseAlpha">
              <feFuncA type="table" tableValues="1 0" />
            </feComponentTransfer>
            <feGaussianBlur
              in="inverseAlpha"
              stdDeviation={SHADOW_BLUR}
              result="blurred"
            />
            <feOffset in="blurred" dy={SHADOW_OFFSET_Y} result="offsetblur" />
            <feFlood
              floodColor={colors.text.black}
              floodOpacity={SHADOW_OPACITY}
              result="shadowColor"
            />
            <feComposite
              in="shadowColor"
              in2="offsetblur"
              operator="in"
              result="shadowShape"
            />
            <feComposite
              in="shadowShape"
              in2="SourceAlpha"
              operator="in"
              result="innerShadow"
            />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="innerShadow" />
            </feMerge>
          </filter>

          <filter
            id={knobShadowId}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feDropShadow
              dx={KNOB_SHADOW_OFFSET_X}
              dy={KNOB_SHADOW_OFFSET_Y}
              stdDeviation={KNOB_SHADOW_BLUR}
              floodColor={colors.text.black}
              floodOpacity={KNOB_SHADOW_OPACITY}
            />
          </filter>
        </defs>

        <path
          d={ARC_PATH}
          fill="none"
          stroke={colors.neutral[10]}
          strokeWidth={ARC_STROKE_WIDTH}
          strokeLinecap="round"
          filter={`url(#${shadowId})`}
        />

        {safeValue > 0 && (
          <motion.path
            d={ARC_PATH}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={ARC_STROKE_WIDTH}
            strokeLinecap="round"
            pathLength={ARC_LENGTH}
            style={{ strokeDasharray: dashArray }}
          />
        )}

        <motion.circle
          initial={false}
          animate={{ cx: knobX, cy: knobY }}
          transition={VALUE_ANIMATION}
          r={KNOB_RADIUS}
          fill={`url(#${gradientId})`}
          stroke={colors.surface.DEFAULT}
          strokeWidth={KNOB_STROKE_WIDTH}
          filter={`url(#${knobShadowId})`}
        />

        <motion.text
          x="100"
          y="62"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="25"
          fontWeight={600}
          fill={colors.text[60]}
        >
          {displayText}
        </motion.text>
        <text
          x="100"
          y="90"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="15"
          fontWeight={400}
          fill={colors.neutral[50]}
        >
          {label}
        </text>
      </svg>
    </div>
  );
};
